import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, formatApiError } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { EnvelopeSimple, Lock, Eye, EyeSlash, Buildings, GlobeSimple } from '@phosphor-icons/react';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const toggleLang = () => {
    const next = i18n.language === 'it' ? 'es' : 'it';
    i18n.changeLanguage(next);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{ backgroundImage: 'url(https://images.pexels.com/photos/12903905/pexels-photo-12903905.jpeg)' }}
      >
        <div className="absolute inset-0 bg-[#0033A0]/80" />
        <div className="relative z-10 flex flex-col justify-center px-12 text-white">
          <div className="flex items-center gap-3 mb-8">
            <Buildings size={48} weight="duotone" />
            <span className="font-outfit text-4xl font-black tracking-tight">Infismart</span>
          </div>
          <h1 className="font-outfit text-3xl font-bold mb-4">
            {i18n.language === 'es' ? 'Gestiona presupuestos y clientes de forma profesional' : 'Gestisci preventivi e clienti in modo professionale'}
          </h1>
          <p className="text-lg opacity-90">
            {i18n.language === 'es' ? 'La plataforma completa para empresas de carpinteria. Crea presupuestos en menos de 5 minutos.' : 'La piattaforma completa per aziende di serramenti. Crea preventivi in meno di 5 minuti.'}
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-white relative">
        {/* Language switcher */}
        <button
          onClick={toggleLang}
          className="absolute top-4 right-4 flex items-center gap-1.5 text-sm text-[#4A5568] hover:text-[#0033A0] transition-colors px-3 py-1.5 rounded-sm border border-[#E2E8F0] hover:border-[#0033A0]"
          data-testid="login-language-switcher"
        >
          <GlobeSimple size={16} />
          <span className="uppercase font-bold text-xs">{i18n.language}</span>
        </button>

        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-8 justify-center">
            <Buildings size={36} weight="duotone" className="text-[#0033A0]" />
            <span className="font-outfit text-2xl font-black text-[#0033A0]">Infismart</span>
          </div>

          <h2 className="font-outfit text-2xl font-bold text-[#0A0F1A] mb-2">
            {i18n.language === 'es' ? 'Accede a tu cuenta' : 'Accedi al tuo account'}
          </h2>
          <p className="text-[#4A5568] mb-8">
            {i18n.language === 'es' ? 'Introduce tus credenciales' : 'Inserisci le tue credenziali per accedere'}
          </p>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-sm text-red-700 text-sm" data-testid="login-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email" className="text-[#0A0F1A] font-medium">
                Email
              </Label>
              <div className="mt-2 relative">
                <EnvelopeSimple className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5568]" size={20} />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 h-11 border-[#E2E8F0] focus:ring-2 focus:ring-[#0033A0] focus:border-transparent"
                  placeholder="nome@azienda.com"
                  required
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-[#0A0F1A] font-medium">
                {t('auth.password')}
              </Label>
              <div className="mt-2 relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5568]" size={20} />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10 h-11 border-[#E2E8F0] focus:ring-2 focus:ring-[#0033A0] focus:border-transparent"
                  placeholder="••••••••"
                  required
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4A5568] hover:text-[#0A0F1A]"
                >
                  {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-[#0033A0] hover:bg-[#0047AB] text-white font-medium"
              data-testid="login-submit-button"
            >
              {loading ? (i18n.language === 'es' ? 'Accediendo...' : 'Accesso in corso...') : t('auth.login')}
            </Button>
          </form>

          <p className="mt-8 text-center text-[#4A5568]">
            {t('auth.noAccount')}{' '}
            <Link to="/register" className="text-[#0033A0] hover:underline font-medium" data-testid="register-link">
              {t('auth.register')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

